import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { GoogleDriveStatus } from "../types";

/** Connect a corporate Google account so receipt scans land in Drive. */
export function GoogleDrivePanel() {
  const [status, setStatus] = useState<GoogleDriveStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadStatus() {
    try {
      setStatus(await api.request<GoogleDriveStatus>("/gdrive/status"));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read Drive status");
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("gdrive");
    if (result === "connected") {
      setMessage("Google Drive connected. New scans will be stored there.");
    } else if (result === "error") {
      setError(`Connecting failed (${params.get("reason") || "unknown"}).`);
    }
    if (result) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    void loadStatus();
  }, []);

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.request<GoogleDriveStatus>("/gdrive/connect", {
        method: "POST",
      });
      if (!res.authorize_url) throw new Error("No authorization URL returned");
      window.location.href = res.authorize_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start the connection");
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect Google Drive? Existing files stay in Drive.")) return;
    setBusy(true);
    setError(null);
    try {
      await api.request("/gdrive/disconnect", { method: "POST" });
      setMessage("Google Drive disconnected. New scans stay on the server.");
      await loadStatus();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not disconnect");
    } finally {
      setBusy(false);
    }
  }

  if (!status) {
    return (
      <div className="panel stack" style={{ marginTop: "1rem" }}>
        <h3 style={{ fontSize: "1.05rem", margin: 0 }}>Google Drive</h3>
        <p className="muted" style={{ margin: 0 }}>
          Loading…
        </p>
      </div>
    );
  }

  return (
    <div className="panel stack" style={{ marginTop: "1rem" }}>
      <div className="row between" style={{ alignItems: "flex-start" }}>
        <div>
          <h3 style={{ fontSize: "1.05rem", margin: 0 }}>Google Drive</h3>
          <p className="muted" style={{ margin: "0.25rem 0 0", fontSize: "0.85rem" }}>
            Connect your own Google account. Scans go into My Drive under
            Receipt Matching → year → month → username.
          </p>
        </div>
        {status.connected && (
          <span className="badge badge-matched">
            {status.auth_mode === "oauth" ? "Connected" : "Service account"}
          </span>
        )}
      </div>

      {message && (
        <div className="alert alert-ok" style={{ margin: 0 }}>
          {message}
        </div>
      )}
      {error && (
        <div className="alert" style={{ margin: 0 }}>
          {error}
        </div>
      )}

      {!status.configured && !status.connected && (
        <p className="muted" style={{ margin: 0, fontSize: "0.9rem" }}>
          One-time setup: create a Google OAuth client for your account, put the
          client id/secret in <code>.env</code>, restart the API, then Connect.
          Steps: <code>docs/google-drive-storage.md</code>.
        </p>
      )}

      {status.connected && (
        <div className="muted" style={{ fontSize: "0.9rem" }}>
          {status.folder_name && (
            <div>
              Folder: <strong>{status.folder_name}</strong>
            </div>
          )}
          {status.folder_id && (
            <div className="mono" style={{ fontSize: "0.75rem", marginTop: "0.2rem" }}>
              id {status.folder_id}
            </div>
          )}
        </div>
      )}

      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
        {status.configured && status.auth_mode !== "oauth" && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={connect}
          >
            {busy ? "Opening Google…" : "Connect Google Drive"}
          </button>
        )}
        {status.auth_mode === "oauth" && (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={disconnect}
          >
            Disconnect
          </button>
        )}
      </div>
    </div>
  );
}
